import type { ChartGeometry, ChartSeries, ChartSpec } from './types';
import { CHART_SPEC_CONTRACT_HASH } from './contract';
import { z } from 'zod';

const MARKS = new Set(['line', 'bar', 'area', 'scatter', 'candlestick']);
const PROFILES = new Set(['financial', 'analytical', 'comparison']);
const TIME_SCALE_MODES = new Set(['session', 'calendar', 'ordinal']);
const markerIdentifierSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z][A-Za-z0-9_-]*$/);
const markerDatasetReferencesSchema = z
  .array(
    z
      .object({
        id: markerIdentifierSchema,
        datasetId: markerIdentifierSchema,
        paneId: markerIdentifierSchema,
        seriesId: markerIdentifierSchema,
        layerId: markerIdentifierSchema.optional(),
        fieldMapping: z
          .object({
            id: markerIdentifierSchema,
            time: markerIdentifierSchema,
            price: markerIdentifierSchema.optional(),
            symbol: markerIdentifierSchema.optional(),
            side: markerIdentifierSchema.optional(),
            quantity: markerIdentifierSchema.optional(),
          })
          .strict(),
      })
      .strict(),
  )
  .max(30);

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function numericFieldExists(spec: ChartSpec, key: string): boolean {
  return spec.data.some((row) => typeof row[key] === 'number');
}

export function validateChartSpec(value: unknown): string[] {
  if (!value || typeof value !== 'object') return ['Chart payload is not an object.'];
  const spec = value as Partial<ChartSpec>;
  const errors: string[] = [];

  if (spec.kind !== 'timeseries' && spec.kind !== 'category') {
    errors.push('Chart kind must be timeseries or category.');
  }
  if (spec.contractHash && spec.contractHash !== CHART_SPEC_CONTRACT_HASH) {
    errors.push('Chart contract hash does not match this renderer.');
  }
  if (!hasText(spec.title)) errors.push('Chart title is required.');
  if (spec.profile && !PROFILES.has(spec.profile)) {
    errors.push('Chart profile is invalid.');
  }
  if (!spec.xAxis || !hasText(spec.xAxis.key) || !hasText(spec.xAxis.type)) {
    errors.push('Chart xAxis requires an explicit key and type.');
  }
  if (!Array.isArray(spec.axes) || spec.axes.length === 0) {
    errors.push('Chart requires at least one explicit axis.');
  }
  if (!Array.isArray(spec.panes) || spec.panes.length === 0) {
    errors.push('Chart requires at least one explicit pane.');
  }
  if (!Array.isArray(spec.series) || spec.series.length === 0) {
    errors.push('Chart requires at least one explicit series.');
  }
  if (!Array.isArray(spec.data) || spec.data.length === 0) {
    errors.push('Chart requires data rows.');
  }
  if (errors.length > 0) return errors;

  const complete = spec as ChartSpec;
  if (complete.xAxis.scaleMode && !TIME_SCALE_MODES.has(complete.xAxis.scaleMode)) {
    errors.push('Chart x-axis scale mode is invalid.');
  }
  const axisIds = new Set(complete.axes.map((axis) => axis.id));
  const paneIds = new Set(complete.panes.map((pane) => pane.id));
  const layerIds = new Set<string>();
  (complete.layers || []).forEach((layer) => {
    if (!hasText(layer.id) || !hasText(layer.label)) {
      errors.push('Every chart layer requires an id and label.');
    } else if (layerIds.has(layer.id)) {
      errors.push(`Chart layer id ${layer.id} is duplicated.`);
    }
    layerIds.add(layer.id);
  });
  const overlayGroupIds = new Set<string>();
  (complete.overlayGroups || []).forEach((group) => {
    if (!hasText(group.id) || !hasText(group.label)) {
      errors.push('Every overlay group requires an id and label.');
    } else if (overlayGroupIds.has(group.id)) {
      errors.push(`Overlay group id ${group.id} is duplicated.`);
    }
    overlayGroupIds.add(group.id);
    layerIds.add(group.id);
  });
  const validateGroup = (groupId: string | undefined, owner: string) => {
    if (groupId && !overlayGroupIds.has(groupId)) {
      errors.push(`${owner} references unknown overlay group ${groupId}.`);
    }
  };
  const validateLayer = (layerId: string | undefined, owner: string) => {
    if (complete.schemaVersion === 2 && !layerId) {
      errors.push(`${owner} requires a layerId in ChartSpec v2.`);
    } else if (layerId && !layerIds.has(layerId)) {
      errors.push(`${owner} references unknown chart layer ${layerId}.`);
    }
  };
  const seriesIds = new Set<string>();
  const paneAxes = new Map(complete.panes.map((pane) => [pane.id, new Set(pane.axisIds)]));

  complete.panes.forEach((pane) => {
    if (!hasText(pane.id) || !Number.isFinite(pane.weight) || pane.weight <= 0) {
      errors.push('Every pane requires an id and positive weight.');
    }
    pane.axisIds.forEach((axisId) => {
      if (!axisIds.has(axisId)) {
        errors.push(`Pane ${pane.id} references unknown axis ${axisId}.`);
      }
    });
  });

  complete.series.forEach((series: ChartSeries) => {
    validateGroup(series.groupId, `Series ${series.id}`);
    validateLayer(series.layerId || series.groupId, `Series ${series.id}`);
    if (!hasText(series.id) || !MARKS.has(series.mark)) {
      errors.push('Every series requires an id and supported mark.');
    }
    if (seriesIds.has(series.id)) {
      errors.push(`Series id ${series.id} is duplicated.`);
    }
    seriesIds.add(series.id);
    if (!paneIds.has(series.paneId)) {
      errors.push(`Series ${series.id} references unknown pane ${series.paneId}.`);
    }
    if (!axisIds.has(series.axisId) || !paneAxes.get(series.paneId)?.has(series.axisId)) {
      errors.push(
        `Series ${series.id} axis ${series.axisId} is not assigned to pane ${series.paneId}.`,
      );
    }
    if (series.mark === 'candlestick') {
      const mapping = series.fieldMapping;
      if (!mapping?.open || !mapping.high || !mapping.low || !mapping.close) {
        errors.push(
          `Candlestick series ${series.id} requires open, high, low, and close mappings.`,
        );
      } else {
        Object.values(mapping).forEach((key) => {
          if (!numericFieldExists(complete, key)) {
            errors.push(`Candlestick series ${series.id} field ${key} has no numeric values.`);
          }
        });
      }
    } else if (!series.dataKey) {
      errors.push(`Series ${series.id} requires an explicit dataKey.`);
    } else if (!numericFieldExists(complete, series.dataKey)) {
      errors.push(`Series ${series.id} field ${series.dataKey} has no numeric values.`);
    }
    if (series.barColors && series.mark !== 'bar') {
      errors.push(`Series ${series.id} uses bar colors without a bar mark.`);
    }
    if (series.barColors?.mode === 'direction' && !series.barColors.closeKey) {
      errors.push(`Series ${series.id} direction colors require closeKey.`);
    }
    if (series.barColors?.mode === 'field' && !series.barColors.colorKey) {
      errors.push(`Series ${series.id} field colors require colorKey.`);
    }
    if (series.placement === 'price-volume-overlay' && series.role !== 'volume') {
      errors.push(`Series ${series.id} price-volume-overlay placement requires the volume role.`);
    }
    if (series.placement === 'price-volume-overlay' && series.mark !== 'bar') {
      errors.push(`Series ${series.id} price-volume-overlay placement requires a bar mark.`);
    }
    if (
      series.overlayHeightRatio !== undefined &&
      (series.placement !== 'price-volume-overlay' ||
        series.overlayHeightRatio < 0.15 ||
        series.overlayHeightRatio > 0.35)
    ) {
      errors.push(`Series ${series.id} has an invalid volume overlay height.`);
    }
  });

  const pricePaneIds = new Set(
    complete.series.filter((series) => series.role === 'price').map((series) => series.paneId),
  );
  complete.series.forEach((series) => {
    if (series.role !== 'volume') return;
    if (complete.profile !== 'financial' && complete.profile !== 'comparison') {
      errors.push(`Series ${series.id} volume role requires a financial or comparison profile.`);
    }
    if (series.placement !== 'price-volume-overlay') {
      errors.push(`Series ${series.id} volume role requires price-volume-overlay placement.`);
    }
    if (!pricePaneIds.has(series.paneId)) {
      errors.push(`Series ${series.id} must share a pane with an explicit price series.`);
    }
  });

  complete.data.forEach((row, index) => {
    if (!(complete.xAxis.key in row)) {
      errors.push(`Data row ${index + 1} is missing x-axis field ${complete.xAxis.key}.`);
    }
  });

  (complete.referenceLines || []).forEach((line) => {
    validateGroup(line.groupId, `Reference line ${line.id}`);
    validateLayer(line.layerId || line.groupId, `Reference line ${line.id}`);
    if (!paneIds.has(line.paneId) || !paneAxes.get(line.paneId)?.has(line.axisId)) {
      errors.push(`Reference line ${line.id} has an invalid pane or axis.`);
    }
    const hasStart = line.xStart !== undefined;
    const hasEnd = line.xEnd !== undefined;
    if (hasStart !== hasEnd) {
      errors.push(`Reference line ${line.id} requires both xStart and xEnd.`);
    }
    if (
      hasStart &&
      hasEnd &&
      !complete.data.some((row) => row[complete.xAxis.key] === line.xStart)
    ) {
      errors.push(`Reference line ${line.id} xStart is not present in chart data.`);
    }
    if (hasStart && hasEnd && !complete.data.some((row) => row[complete.xAxis.key] === line.xEnd)) {
      errors.push(`Reference line ${line.id} xEnd is not present in chart data.`);
    }
  });
  (complete.markers || []).forEach((marker) => {
    validateGroup(marker.groupId, `Marker ${marker.id}`);
    validateLayer(marker.layerId || marker.groupId, `Marker ${marker.id}`);
    if (!paneIds.has(marker.paneId) || !seriesIds.has(marker.seriesId)) {
      errors.push(`Marker ${marker.id} has an invalid pane or series.`);
    }
  });
  if (complete.markerDatasets !== undefined) {
    const references = markerDatasetReferencesSchema.safeParse(complete.markerDatasets);
    if (!references.success) {
      references.error.issues.forEach((issue) => {
        errors.push(`Marker datasets ${issue.path.join('.') || 'collection'}: ${issue.message}`);
      });
    } else {
      const referenceIds = new Set<string>();
      references.data.forEach((reference) => {
        if (referenceIds.has(reference.id)) {
          errors.push(`Marker dataset reference id ${reference.id} is duplicated.`);
        }
        referenceIds.add(reference.id);
        validateLayer(reference.layerId, `Marker dataset ${reference.id}`);
        const series = complete.series.find((item) => item.id === reference.seriesId);
        if (!paneIds.has(reference.paneId) || !series || series.paneId !== reference.paneId) {
          errors.push(`Marker dataset ${reference.id} must reference a series in its pane.`);
        }
      });
    }
  }
  (complete.referenceBands || []).forEach((band) => {
    validateGroup(band.groupId, `Reference band ${band.id}`);
    validateLayer(band.layerId || band.groupId, `Reference band ${band.id}`);
    if (!paneIds.has(band.paneId) || !paneAxes.get(band.paneId)?.has(band.axisId)) {
      errors.push(`Reference band ${band.id} has an invalid pane or axis.`);
    }
  });
  (complete.eventWindows || []).forEach((window) => {
    validateGroup(window.groupId, `Event window ${window.id}`);
    validateLayer(window.layerId || window.groupId, `Event window ${window.id}`);
    if (!paneIds.has(window.paneId)) {
      errors.push(`Event window ${window.id} has an invalid pane.`);
    }
  });
  (complete.areas || []).forEach((area) => {
    validateGroup(area.groupId, `Area ${area.id}`);
    validateLayer(area.layerId || area.groupId, `Area ${area.id}`);
    if (!paneIds.has(area.paneId)) {
      errors.push(`Area ${area.id} has an invalid pane.`);
    }
    if (
      area.axisId &&
      (!axisIds.has(area.axisId) || !paneAxes.get(area.paneId)?.has(area.axisId))
    ) {
      errors.push(`Area ${area.id} has an invalid axis.`);
    }
    const hasXStart = area.xStart !== undefined;
    const hasXEnd = area.xEnd !== undefined;
    const hasYStart = area.yStart !== undefined;
    const hasYEnd = area.yEnd !== undefined;
    if (hasXStart !== hasXEnd) errors.push(`Area ${area.id} requires both xStart and xEnd.`);
    if (hasYStart !== hasYEnd) errors.push(`Area ${area.id} requires both yStart and yEnd.`);
    if (!hasXStart && !hasYStart)
      errors.push(`Area ${area.id} requires x bounds, y bounds, or both.`);
  });
  const transformIds = new Set((complete.transforms || []).map((transform) => transform.id));
  complete.series.forEach((series) => {
    if (series.transformId && !transformIds.has(series.transformId)) {
      errors.push(`Series ${series.id} references unknown transform ${series.transformId}.`);
    }
  });
  (complete.hoverFields || []).forEach((field) => {
    if (!paneIds.has(field.paneId) || !complete.data.some((row) => field.dataKey in row)) {
      errors.push(`Hover field ${field.id} has an invalid pane or data key.`);
    }
  });

  return errors;
}

export function chartGeometry(spec: ChartSpec): ChartGeometry {
  return {
    kind: spec.kind,
    xAxis: spec.xAxis,
    axes: spec.axes.map(({ id, side, format, domain }) => ({
      id,
      side,
      format,
      domain,
    })),
    panes: spec.panes.map(({ id, weight, axisIds }) => ({
      id,
      weight,
      axisIds,
    })),
    series: spec.series.map(
      ({
        id,
        semanticElementId,
        role,
        mark,
        paneId,
        axisId,
        dataKey,
        fieldMapping,
        placement,
        overlayHeightRatio,
      }) => ({
        id,
        semanticElementId,
        role,
        mark,
        paneId,
        axisId,
        dataKey,
        fieldMapping,
        placement,
        overlayHeightRatio,
      }),
    ),
    referenceLines: (spec.referenceLines || []).map(
      ({ id, semanticElementId, paneId, axisId, value, unit, xStart, xEnd }) => ({
        id,
        semanticElementId,
        paneId,
        axisId,
        value,
        unit,
        xStart,
        xEnd,
      }),
    ),
    markers: (spec.markers || []).map(
      ({ id, semanticElementId, paneId, seriesId, x, value, unit, placement }) => ({
        id,
        semanticElementId,
        paneId,
        seriesId,
        x,
        value,
        unit,
        placement,
      }),
    ),
  };
}
